import { useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { draftSave, draftLoad, draftClear } from '../../components/Pick/cache'
import { Alert, SortableRider, NationalFlag, RiderName, stripTeamBracket, RiderIdent } from '../../components/Pick/ui'
import InsightOpener from '../../components/Pick/InsightOpener'
import DeadlineLine from '../../components/Pick/DeadlineLine'
import RiderInsightSheet from '../../components/Pick/RiderInsightSheet'
import RiderFace from '../../components/RiderFace'
import { loadRiderInsights } from '../../lib/riderInsights'
import { pickGuard } from '../../lib/pickGuard'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import PickToast from './PickToast'
import GradientButton from '../../components/GradientButton'
import ModaleSelezione from './ModaleSelezione'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { hapticLight, hapticMedium, hapticSuccess, hapticError } from '../../lib/haptics'
import PietrePick from './PietrePick'
import { loadNationKits, NAT_TEAM_CODE } from '../../lib/nationKits'

// ─── dnd-kit / DraggableFlatList — lazy init per evitare Platform.OS a livello modulo
let DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors
let SortableContext, verticalListSortingStrategy, arrayMove
let DraggableFlatList
let _dndInited = false

function initDnd() {
  if (_dndInited) return
  _dndInited = true
  if (Platform.OS === 'web') {
    try {
      const core = require('@dnd-kit/core')
      const sortable = require('@dnd-kit/sortable')
      DndContext = core.DndContext
      closestCenter = core.closestCenter
      PointerSensor = core.PointerSensor
      TouchSensor = core.TouchSensor
      useSensor = core.useSensor
      useSensors = core.useSensors
      SortableContext = sortable.SortableContext
      verticalListSortingStrategy = sortable.verticalListSortingStrategy
      arrayMove = sortable.arrayMove
    } catch { }
  } else {
    try {
      // Versione "nestable": la lista sta dentro lo scroll della pagina (NestableScrollContainer in index.jsx),
      // cosi scroll e trascinamento sono coordinati da un solo sistema di gesture invece di due sovrapposti.
      DraggableFlatList = require('react-native-draggable-flatlist').NestableDraggableFlatList
    } catch { }
  }
}

// Righe (id_rider, id_stage) dei corridori gia usati nel giro, escludendo la tappa corrente
async function fetchUsedRowsInTour(userId, raceId, leagueId, currentStageId) {
  const { data, error } = await supabase.rpc('fn_check_duplicate_in_tour', { p_id_user: userId, p_id_race: raceId, p_id_league: leagueId })
  if (error) return []
  return (data ?? []).filter(row => row.id_stage !== currentStageId)
}

async function fetchUsedRidersInTour(userId, raceId, leagueId, currentStageId) {
  const { data, error } = await supabase.rpc('fn_check_duplicate_in_tour', {
    p_id_user: userId, p_id_race: raceId, p_id_league: leagueId,
  })
  if (error) { console.error('[fetchUsedRidersInTour]', error); return [] }
  return (data ?? []).filter(row => row.id_stage !== currentStageId).map(row => row.id_rider)
}

// ─── Un solo corridore (tappe in linea): niente lista trascinabile né numeri di slot ───
function SinglePicked({ rider, onChange, onRemove, isNationalRace }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: tc.rosaSubtle, borderWidth: 1, borderColor: tc.rosa, borderRadius: radius.lg, padding: 12, marginBottom: 12, minHeight: 64 }}>
      <RiderFace avatar={rider.avatar} kit={isNationalRace ? null : rider.kit} size={50} shape="bust" />
      <RiderIdent rider={rider} isNationalRace={isNationalRace} fullName teamMode="short" nameStyle={{ fontSize: 17, color: tc.text }} />
      <TouchableOpacity onPress={onChange} style={{ minHeight: 44, minWidth: 44, alignItems: 'center', justifyContent: 'center' }} accessibilityRole="button" accessibilityLabel={t('pick.edit_selection')}>
        <MaterialCommunityIcons name="swap-horizontal" size={22} color={tc.rosaText} />
      </TouchableOpacity>
      <TouchableOpacity onPress={onRemove} style={{ minHeight: 44, minWidth: 44, alignItems: 'center', justifyContent: 'center' }} accessibilityRole="button" accessibilityLabel={t('pick.draft_cancel')}>
        <MaterialCommunityIcons name="close-circle-outline" size={22} color={tc.textMuted} />
      </TouchableOpacity>
    </View>
  )
}

// ─── Lista corridori selezionati ──────────────────────────────────────
function SelectedList({ selected, isCompact, onRemove, onReorder, isNationalRace }) {
  initDnd()
  if (Platform.OS === 'web' && DndContext) {
    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 15 } }),
      useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 15 } })
    )
    const handleDragEnd = (event) => {
      const { active, over } = event
      if (active.id !== over?.id) {
        const oldIdx = selected.findIndex(r => r.value === active.id)
        const newIdx = selected.findIndex(r => r.value === over.id)
        onReorder(arrayMove(selected, oldIdx, newIdx))
      }
    }
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={selected.map(r => r.value)} strategy={verticalListSortingStrategy}>
          <View style={{ marginBottom: 12 }}>
            {selected.map((rider, i) => (
              <SortableRider
                key={rider.value}
                rider={rider}
                index={i}
                isCompact={isCompact}
                onRemove={onRemove}
                isNationalRace={isNationalRace}
              />
            ))}
          </View>
        </SortableContext>
      </DndContext>
    )
  }

  if (DraggableFlatList) {
    return (
      <DraggableFlatList
        data={selected}
        keyExtractor={item => String(item.value)}
        onDragEnd={({ data }) => { hapticLight(); onReorder(data) }}
        renderItem={({ item, getIndex, drag, isActive }) => (
          <SortableRider
            rider={item}
            index={getIndex()}
            isCompact={isCompact}
            onRemove={onRemove}
            drag={() => { hapticMedium(); drag() }}
            isActive={isActive}
            isNationalRace={isNationalRace}
          />
        )}
      />
    )
  }

  return (
    <View style={{ marginBottom: 12 }}>
      {selected.map((rider, i) => (
        <SortableRider key={rider.value} rider={rider} index={i} isCompact={isCompact} onRemove={onRemove} isNationalRace={isNationalRace} />
      ))}
    </View>
  )
}

export default function InserimentoPick({ userId, selGara, selTappa, leagueId, hasFlip }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const [allRiders, setAllRiders] = useState([])
  const [selected, setSelected] = useState([])
  const [usedRiderIds, setUsedRiderIds] = useState(new Set())
  const [usedStage, setUsedStage] = useState({})        // id_rider -> numero tappa
  const [rulesOpen, setRulesOpen] = useState(false)
  const [modaleOpen, setModaleOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [applyToAll, setApplyToAll] = useState(true)
  const [report, setReport] = useState(null)
  const [toast, setToast] = useState(null)
  const [draftRestored, setDraftRestored] = useState(false)
  const [loadDone, setLoadDone] = useState(false)
  const [saved, setSaved] = useState(false)
  const [savedIds, setSavedIds] = useState([])          // corridori della pick salvata nel DB, in ordine di slot
  const [insights, setInsights] = useState({})       // id_rider -> insight (fn_rider_insights)
  const [insightOpen, setInsightOpen] = useState(false)

  // Flip
  const [flipRider, setFlipRider] = useState(null)
  const [flipSlot, setFlipSlot] = useState(null)
  const [flipModaleOpen, setFlipModaleOpen] = useState(false)
  const [existingFlip, setExistingFlip] = useState(null)

  // Categoria gara (per riconoscere le gare di nazionali, id_race_category === 5)
  const [raceCategory, setRaceCategory] = useState(null)
  const isNationalRace = raceCategory === 5

  // Nomi estesi delle nazionalità (dim_national.name), per il modale di
  // selezione nelle gare di nazionali — {id_national: name}
  const [nationalNames, setNationalNames] = useState({})

  const isODR = selGara?.type === 3

  const limit = selTappa?.pick_limit ? parseInt(selTappa.pick_limit) : selGara?.type === 3 ? 5 : 1
  const isCompact = limit > 3

  const isPietre = leagueId === 4

  // Carica una volta sola i nomi estesi delle nazionalità (dim_national),
  // usati dal modale di selezione al posto del solo codice ISO (AU, BE...)
  // nelle gare di nazionali.
  useEffect(() => {
    let cancelled = false
    supabase.from('dim_national').select('id_national, name').then(({ data, error }) => {
      if (cancelled || error || !data) return
      setNationalNames(Object.fromEntries(data.map(n => [n.id_national, n.name])))
    })
    return () => { cancelled = true }
  }, [])

  // ── Bozza: la selezione si salva da sola sul dispositivo e riappare (con il banner) se esci senza confermare ──
  useEffect(() => {
    if (!userId || !selTappa?.id_stage || !leagueId || !loadDone) return
    draftSave(userId, selTappa.id_stage, leagueId, selected, selGara?.id)
  }, [selected, userId, selTappa?.id_stage, leagueId, loadDone, selGara?.id])

  // ── Modifiche non salvate: la selezione attuale e' DIVERSA dalla pick salvata (confronto degli id, in ordine di slot) ──
  const selectedKey = selected.map(r => r.value).join(',')
  // i corridori scelti sono copie fatte al caricamento: avatar e kit arrivano dopo (seconda richiesta), quindi li ripesco da allRiders per mostrarli
  const selectedView = useMemo(() => {
    const byId = new Map(allRiders.map(r => [r.value, r]))
    return selected.map(r => { const f = byId.get(r.value); return f ? { ...r, avatar: f.avatar ?? null, kit: f.kit ?? null, teamShort: f.teamShort ?? null, teamName: f.teamName ?? null, bib: r.bib ?? f.bib } : r })
  }, [selected, allRiders])
  const dirty = loadDone && selected.length > 0 && selectedKey !== savedIds.join(',')
  useEffect(() => { pickGuard.dirty = dirty }, [dirty])
  useEffect(() => { pickGuard.ack = false }, [selectedKey])         // cambia la selezione: l'avviso torna possibile
  useEffect(() => () => { pickGuard.dirty = false; pickGuard.ack = false }, [])   // la pagina si smonta: niente stato residuo
  // Web: chiudere o ricaricare la scheda con modifiche non salvate
  useEffect(() => {
    if (Platform.OS !== 'web' || !dirty || typeof window === 'undefined') return undefined
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  // Insight sui corridori della start list: le tue scelte passate e la loro forma
  useEffect(() => {
    if (!userId || !selTappa?.id_stage || allRiders.length === 0) return
    let cancelled = false
    loadRiderInsights(userId, selTappa.id_stage, allRiders.map(r => r.value)).then(m => { if (!cancelled) setInsights(m) }).catch(() => {})
    return () => { cancelled = true }
  }, [userId, selTappa?.id_stage, allRiders])

  // Load
  useEffect(() => {
    if (!userId || !selGara?.id || !selTappa?.id_stage) return
    let cancelled = false
    setLoadDone(false)
    const load = async () => {
      const [rRes, pRes, catRes] = await Promise.all([
        supabase.from('view_start_list_display')
          .select('id_rider, rider_name, rider_surname, rider_nam, id_team, id_national,bib, rider_name_new, uci_code')
          .eq('id_race', selGara.id).order('rider_name'),
        supabase.from('fact_user_pick').select('id_slot, id_rider').eq('id_user', userId).eq('id_stage', selTappa.id_stage).eq('id_league', leagueId).order('id_slot'),
        supabase.from('dim_race').select('id_race_category').eq('id_race', selGara.id).maybeSingle(),
      ])
      if (cancelled) return
      setRaceCategory(catRes.data?.id_race_category ?? null)
      const riders = (rRes.data ?? []).map(r => ({
        value: r.id_rider,
        label: `${r.rider_surname} ${r.rider_nam}`,
        label_raw: r.rider_name,
        label_new: r.rider_name_new,
        surname: r.rider_surname,
        firstName: r.rider_nam,
        id_team: r.id_team,
        key: String(r.id_rider),
        id_national: r.id_national,
        bib: r.bib,   // numero di gara: ordina la lista (ModaleSelezione) e compare nel dorsale (BibTag)
        uci_code: r.uci_code,
      }))
      setAllRiders(riders)

      // Volti dei corridori (dim_rider.avatar) e kit delle squadre: in seconda battuta, la lista appare subito e i volti arrivano dopo
      const riderIds = riders.map(r => r.value)
      const teamIds = [...new Set(riders.map(r => r.id_team).filter(t => typeof t === 'number'))]
      Promise.all([
        supabase.from('dim_rider').select('id_rider, avatar').in('id_rider', riderIds).not('avatar', 'is', null),
        teamIds.length ? supabase.from('dim_team').select('id_team, kit, name, short_txt').in('id_team', teamIds) : Promise.resolve({ data: [] }),
        loadNationKits(),
      ]).then(([avRes, kitRes, nations]) => {
        if (cancelled) return
        const av = Object.fromEntries((avRes.data ?? []).map(x => [x.id_rider, x.avatar]))
        const kits = Object.fromEntries((kitRes.data ?? []).map(x => [x.id_team, x.kit]))
        const teamsTxt = Object.fromEntries((kitRes.data ?? []).map(x => [x.id_team, x]))
        // kit per tutti (maglia della squadra al posto di jersey_url); avatar solo dove c'e': gli altri vedono il template (components/RiderFace.jsx)
        // squadra generica NAT (corridori "locali" senza codice squadra): maglia, sigla e nome del loro paese, se il paese ha il kit
        setAllRiders(prev => prev.map(r => {
          const nat = r.uci_code === NAT_TEAM_CODE && nations[r.id_national]?.kit ? nations[r.id_national] : null
          return { ...r, avatar: av[r.value] ?? null, kit: nat?.kit ?? kits[r.id_team] ?? null, teamShort: nat?.id_national_3 ?? teamsTxt[r.id_team]?.short_txt ?? null, teamName: nat?.name ?? teamsTxt[r.id_team]?.name ?? null }
        }))
      }).catch(() => {})


      if (hasFlip) {
        const { data: fData } = await supabase.from('fact_user_flip').select('id_flip, id_rider, id_slot, id_stage_used, id_team').eq('id_user', userId).eq('id_race', selGara.id).eq('id_league', leagueId).maybeSingle()
        if (cancelled) return
        if (fData) { setExistingFlip(fData); const flipR = riders.find(r => r.value === fData.id_rider); if (flipR) { setFlipRider(flipR); setFlipSlot(fData.id_slot) } }
        else { setExistingFlip(null); setFlipRider(null); setFlipSlot(null) }
      }

      const savedPicks = (pRes.data ?? []).sort((a, b) => a.id_slot - b.id_slot).map(p => riders.find(r => r.value === p.id_rider)).filter(Boolean)
      if (savedPicks.length > 0) {
        setSelected(savedPicks.map(r => ({ ...r, key: String(r.value) })))
        setDraftRestored(false)
        setSavedIds(savedPicks.map(r => r.value))
        setSaved(true)
        await draftClear(userId, selTappa.id_stage, leagueId)
      } else {
        setSavedIds([])
        const draft = await draftLoad(userId, selTappa.id_stage, leagueId)
        if (draft?.length > 0) {
          const valid = draft.filter(d => riders.find(r => r.value === d.value))
          if (valid.length > 0) { setSelected(valid.map(r => ({ ...r, key: String(r.value) }))); setDraftRestored(true) }
        }
      }
      setLoadDone(true)
    }
    load()
    return () => { cancelled = true }
  }, [userId, selGara?.id, selTappa?.id_stage, leagueId, hasFlip])

  // Corridori già usati in questa lega nel resto del tour (per marcarli
  // come "bloccati" nel modale di selezione, prima ancora della conferma)
  useEffect(() => {
    if (!userId || !selGara?.id || !selTappa?.id_stage || !leagueId) return
    let cancelled = false
    Promise.all([
      fetchUsedRowsInTour(userId, selGara.id, leagueId, selTappa.id_stage),
      supabase.from('dim_race_stage').select('id_stage, id_stage_number').eq('id_race', selGara.id),
    ]).then(([rowsUsed, stRes]) => {
      if (cancelled) return
      const num = Object.fromEntries((stRes.data ?? []).map(x => [x.id_stage, x.id_stage_number]))
      setUsedRiderIds(new Set(rowsUsed.map(r => r.id_rider)))
      setUsedStage(Object.fromEntries(rowsUsed.map(r => [r.id_rider, num[r.id_stage]])))
    })
    return () => { cancelled = true }
  }, [userId, selGara?.id, selTappa?.id_stage, leagueId])

  const addRider = (rider) => {
    if (selected.find(s => s.value === rider.value)) return
    // un solo corridore: il nuovo sostituisce quello scelto e il modale si chiude
    if (limit === 1) {
      hapticLight()
      setSelected([{ ...rider, key: String(rider.value) }])
      setModaleOpen(false)
      setMsg(null); setReport(null); setDraftRestored(false); setSaved(false)
      return
    }
    if (selected.length >= limit) return
    hapticLight()
    setSelected(prev => [...prev, { ...rider, key: String(rider.value) }])
    setMsg(null); setReport(null); setDraftRestored(false); setSaved(false)
  }

  const removeRider = (idx) => {
    hapticLight()
    setSelected(prev => prev.filter((_, i) => i !== idx))
    setMsg(null); setReport(null); setDraftRestored(false); setSaved(false)
  }

  const handleSaveFlip = async () => {
    if (!flipRider || flipSlot === null) return
    if (selGara.type !== 3) {
      const usedIds = await fetchUsedRidersInTour(userId, selGara.id, leagueId, selTappa.id_stage)
      if (usedIds.includes(flipRider.value)) {
        hapticError()
        setMsg({ type: 'error', text: `${flipRider.label} è già stato usato in questo tour` })
        return
      }
    }
    try {
      if (existingFlip) await supabase.from('fact_user_flip').delete().eq('id_flip', existingFlip.id_flip)
      const { error } = await supabase.from('fact_user_flip').insert({ id_user: userId, id_race: selGara.id, id_stage_used: selTappa.id_stage, id_league: leagueId, id_rider: flipRider.value, id_team: flipRider.id_team, id_slot: flipSlot })
      if (error) throw error
      const { data: fData } = await supabase.from('fact_user_flip').select('id_flip, id_rider, id_slot, id_stage_used, id_team').eq('id_user', userId).eq('id_race', selGara.id).eq('id_league', leagueId).maybeSingle()
      setExistingFlip(fData)
      hapticSuccess()
      setMsg({ type: 'success', text: t('pick.flip_saved') })
    } catch (e) {
      hapticError()
      setMsg({ type: 'error', text: e.message })
    }
  }

  const handleDeleteFlip = async () => {
    if (!existingFlip) return
    try {
      await supabase.from('fact_user_flip').delete().eq('id_flip', existingFlip.id_flip)
      setExistingFlip(null); setFlipRider(null); setFlipSlot(null)
      hapticSuccess()
      setMsg({ type: 'success', text: t('pick.flip_deleted') })
    } catch (e) {
      hapticError()
      setMsg({ type: 'error', text: e.message })
    }
  }

  // Leghe in cui finisce la scelta con "applica a tutte": quelle dell'utente che hanno questa gara in calendario (la corrente c'e' sempre)
  const fetchTargetLeagues = async () => {
    const { data: userLeagues } = await supabase.from('view_user_league').select('id_league, name_league').eq('id_user', userId)
    if (!userLeagues?.length) return []
    const { data: cal } = await supabase.from('fact_league_calendar').select('id_league')
      .eq('id_race', selGara.id).in('id_league', userLeagues.map(l => l.id_league))
    const withRace = new Set((cal ?? []).map(c => c.id_league))
    withRace.add(leagueId)
    return userLeagues.filter(l => withRace.has(l.id_league)).map(l => ({ id: l.id_league, name: l.name_league }))
  }
  const [targetCount, setTargetCount] = useState(null)       // null = non ancora noto
  useEffect(() => {
    if (!userId || !selGara?.id) return
    let cancelled = false
    fetchTargetLeagues().then(l => { if (!cancelled) setTargetCount(l.length) }).catch(() => {})
    return () => { cancelled = true }
  }, [userId, selGara?.id, leagueId])

  const handleSave = async () => {
    setMsg(null); setReport(null)
    if (selected.length < limit) {
      hapticError()
      setMsg({ type: 'error', text: t('pick.need_more', { count: limit - selected.length }) })
      return
    }
    setSaving(true)
    try {
      let leaguesToProcess = [{ id: leagueId, name: t('pick.current_league') }]
      if (applyToAll) {
        const targets = await fetchTargetLeagues()
        if (targets.length > 0) leaguesToProcess = targets
      }
      const results = { success: [], failed: [] }
      await Promise.all(leaguesToProcess.map(async (target) => {
        if (selGara.type !== 3) {
          const usedIds = await fetchUsedRidersInTour(userId, selGara.id, target.id, selTappa.id_stage)
          const dupeRider = selected.find(p => usedIds.includes(p.value))
          if (dupeRider) {
            results.failed.push({
              name: target.name,
              reason: t('pick.duplicate_rider_reason', { name: dupeRider.label })
            }); return
          }
        }
        const { error: upsertErr } = await supabase.from('fact_user_pick').upsert(
          selected.map((r, i) => ({ id_user: userId, id_race: selGara.id, id_stage: selTappa.id_stage, id_rider: r.value, id_team: r.id_team, id_slot: i + 1, id_league: target.id })),
          { onConflict: 'id_user,id_stage,id_slot,id_league' }
        )
        if (upsertErr) results.failed.push({ name: target.name, reason: upsertErr.message })
        else results.success.push(target.name)
      }))
      if (results.failed.length === 0) {
        await draftClear(userId, selTappa.id_stage, leagueId)
        setSavedIds(selected.map(r => r.value))
        setSaved(true)
        setDraftRestored(false); setMsg(null)
        hapticSuccess()
        setToast({ riders: selected, leagueCount: results.success.length })
      } else {
        hapticError()
        setReport(results)
      }
    } catch {
      hapticError()
      setMsg({ type: 'error', text: t('pick.error_save') })
    } finally { setSaving(false) }
  }

  if (allRiders.length === 0) return <View style={{ padding: 16 }}><Text style={{ color: tc.textMuted, fontSize: 13 }}>{t('pick.loading_riders')}</Text></View>

  return (
    <View style={styles.container}>
      {toast && <PickToast riders={toast.riders} leagueCount={toast.leagueCount} onDone={() => setToast(null)} />}
      {insightOpen && selected.length > 0 && <RiderInsightSheet riders={selectedView} insights={insights} isNationalRace={isNationalRace} onClose={() => setInsightOpen(false)} />}
      {modaleOpen && <ModaleSelezione insights={insights} allRiders={allRiders} selected={selected} limit={limit} onAdd={addRider} onRemove={removeRider} onClose={() => setModaleOpen(false)} usedRiderIds={usedRiderIds} usedStage={usedStage} isNationalRace={isNationalRace} nationalNames={nationalNames} />}
      {flipModaleOpen && <ModaleSelezione insights={insights} allRiders={allRiders.filter(r => !selected.find(s => s.value === r.value))} selected={flipRider ? [flipRider] : []} limit={1} onAdd={(r) => { setFlipRider(r); setFlipSlot(null); setFlipModaleOpen(false) }} onRemove={() => { setFlipRider(null); setFlipSlot(null) }} onClose={() => setFlipModaleOpen(false)} isNationalRace={isNationalRace} nationalNames={nationalNames} />}

      {draftRestored && (
        <View style={styles.draftBanner}>
          <Text style={styles.draftIcon}>💾</Text>
          <View style={{ flex: 1 }}>
            <Text style={styles.draftTitle}>{t('pick.draft_restored')}</Text>
            <Text style={styles.draftCount}>{t('pick.draft_count', { count: selected.length })}</Text>
          </View>
          <TouchableOpacity onPress={() => { hapticLight(); setSelected([]); draftClear(userId, selTappa.id_stage, leagueId); setDraftRestored(false) }}>
            <Text style={styles.draftCancel}>{t('pick.draft_cancel')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {selected.length > 0 && loadDone && !draftRestored && !saved && (
        <View style={styles.draftWarningBanner}>
          <Text style={styles.draftWarningText}>⚠️ {t('pick.draft_unsaved_banner')}</Text>
        </View>
      )}

      <DeadlineLine date={selTappa?.stage_date} time={selTappa?.stage_time} />

      <View style={styles.counterRow}>
        <Text style={styles.counterLabel}>{t('pick.select_n', { count: limit })}</Text>
        <Text style={[styles.counterValue, selected.length === limit && styles.counterDone]}>{selected.length}/{limit}</Text>
      </View>

      {selected.length > 0 && limit === 1 && (
        <SinglePicked rider={selectedView[0]} isNationalRace={isNationalRace} onChange={() => { hapticLight(); setModaleOpen(true) }} onRemove={() => removeRider(0)} />
      )}

      {selected.length > 0 && limit > 1 && (
        <SelectedList
          selected={selectedView}
          isCompact={isCompact}
          onRemove={removeRider}
          onReorder={setSelected}
          isNationalRace={isNationalRace}
        />
      )}

      {selected.length < limit && (
        <TouchableOpacity style={styles.addBtn} onPress={() => { hapticLight(); setModaleOpen(true) }}>
          <Text style={styles.addBtnText}>{t('pick.add_rider', { current: selected.length, limit })}</Text>
        </TouchableOpacity>
      )}
      {selected.length === limit && limit > 1 && (
        <TouchableOpacity style={styles.editBtn} onPress={() => { hapticLight(); setModaleOpen(true) }}>
          <Text style={styles.editBtnText}>{t('pick.edit_selection')}</Text>
        </TouchableOpacity>
      )}

      {selected.length > 0 && <InsightOpener onPress={() => setInsightOpen(true)} />}

      {targetCount > 1 && <TouchableOpacity style={styles.applyRow} onPress={() => { hapticLight(); setApplyToAll(!applyToAll) }} accessibilityRole="checkbox" accessibilityState={{ checked: applyToAll }}>
        <View style={[styles.checkbox, applyToAll && styles.checkboxActive]}>
          {applyToAll && <Text style={styles.checkmark}>✓</Text>}
        </View>
        <Text style={styles.applyLabel}>{`${t('pick.apply_all')} (${targetCount})`}</Text>
      </TouchableOpacity>}

      {msg && <Alert type={msg.type}>{msg.text}</Alert>}
      {report && (
        <View style={{ marginBottom: 12 }}>
          {report.success.length > 0 && <Alert type="success">{t('pick.saved_for', { leagues: report.success.join(', ') })}</Alert>}
          {report.failed.map((f, i) => <Alert key={i} type="error">{f.name}: {f.reason}</Alert>)}
        </View>
      )}

      {hasFlip && selected.length > 0 && (
        <View style={styles.flipSection}>
          <View style={styles.flipHeader}>
            <Text style={styles.flipTitle}>🔀 {existingFlip ? t('pick.flip_used') : t('pick.flip_available')}</Text>
            {existingFlip && <TouchableOpacity onPress={handleDeleteFlip}><Text style={styles.flipRemove}>{t('pick.flip_remove')}</Text></TouchableOpacity>}
          </View>
          {existingFlip ? (
            <View style={styles.flipConfirmed}>
              <Text style={styles.flipConfirmedText}>{t('pick.flip_abbinato', { rider: flipRider?.label ?? '—', slot: flipSlot })}</Text>
              <Text style={styles.flipConfirmedSub}>{t('pick.flip_confirmed')}</Text>
            </View>
          ) : (
            <>
              <TouchableOpacity style={[styles.flipAddBtn, flipRider && styles.flipAddBtnActive]} onPress={() => { hapticLight(); setFlipModaleOpen(true) }}>
                <Text style={[styles.flipAddBtnText, flipRider && styles.flipAddBtnTextActive]}>
                  {flipRider ? `🔀 ${flipRider.label}` : t('pick.flip_add_rider')}
                </Text>
              </TouchableOpacity>
              {flipRider && (
                <>
                  <Text style={styles.flipSlotLabel}>{t('pick.flip_slot_label')}</Text>
                  {selected.map((r, i) => (
                    <TouchableOpacity key={i} onPress={() => { hapticLight(); setFlipSlot(i + 1) }} style={[styles.flipSlotBtn, flipSlot === i + 1 && styles.flipSlotBtnActive]}>
                      <Text style={[styles.flipSlotNum, flipSlot === i + 1 && styles.flipSlotNumActive]}>{i + 1}°</Text>
                      <Text style={[styles.flipSlotName, flipSlot === i + 1 && styles.flipSlotNameActive]}>{r.label}</Text>
                      {flipSlot === i + 1 && <Text>🔀</Text>}
                    </TouchableOpacity>
                  ))}
                  {flipSlot && (
                    <GradientButton onPress={handleSaveFlip} size="lg" style={{ width: '100%', marginTop: 4 }}>{t('pick.flip_confirm_btn')}</GradientButton>
                  )}
                </>
              )}
            </>
          )}
        </View>
      )}

      <GradientButton
        onPress={handleSave}
        disabled={selected.length < limit}
        loading={saving}
        size="lg"
        style={{ width: '100%' }}
      >
        {saving ? t('pick.saving') : t('pick.confirm_pick', { current: selected.length, limit })}
      </GradientButton>

      {!isODR && (
        <View style={styles.rulesBox}>
          <TouchableOpacity onPress={() => { hapticLight(); setRulesOpen(o => !o) }} style={styles.rulesHead} accessibilityRole="button" accessibilityState={{ expanded: rulesOpen }}>
            <MaterialCommunityIcons name="information-outline" size={16} color={tc.textMuted} />
            <Text style={styles.rulesTitle}>{t('pick.rules_title')}</Text>
            <MaterialCommunityIcons name={rulesOpen ? 'chevron-up' : 'chevron-down'} size={18} color={tc.textMuted} />
          </TouchableOpacity>
          {rulesOpen && (
            <View style={{ gap: 6, paddingTop: 6 }}>
              {['rules_1', 'rules_2', 'rules_3', ...(hasFlip ? ['rules_flip'] : [])].map(k => (
                <Text key={k} style={styles.rulesText}>• {t(`pick.${k}`)}</Text>
              ))}
            </View>
          )}
        </View>
      )}

      {/* ─── Slot extra Lega Pietre ───────────────────────────────── */}
      {isPietre && (
        <PietrePick
          userId={userId}
          selGara={selGara}
          selTappa={selTappa}
          allRiders={allRiders}
          stageStatus={selTappa?.stage_status}
        />
      )}
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { padding: 8, paddingBottom: 16 },
  draftBanner: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, paddingHorizontal: 14, marginBottom: 12, backgroundColor: 'rgba(245,158,11,0.08)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.3)', borderRadius: radius.lg },
  draftIcon: { fontSize: 20 },
  draftTitle: { fontSize: 13, fontFamily: fonts.condensedBold, color: colors.warning },
  draftCount: { fontSize: 12, color: tc.textMuted, marginTop: 1 },
  draftCancel: { fontSize: 13, fontWeight: '600', color: tc.textMuted },
  draftWarningBanner: { backgroundColor: 'rgba(245,158,11,0.08)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.3)', borderRadius: radius.lg, padding: 10, paddingHorizontal: 14, marginBottom: 10 },
  draftWarningText: { fontSize: 12, color: colors.warning, fontFamily: fonts.condensed },
  rulesBox: { marginTop: 14, marginHorizontal: 8, padding: 12, borderRadius: radius.lg, borderWidth: 1, borderColor: tc.border, backgroundColor: tc.bgCard2 },
  rulesHead: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 28 },
  rulesTitle: { flex: 1, fontSize: 13, fontFamily: fonts.condensedBold, color: tc.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 },
  rulesText: { fontSize: 12, color: tc.textMuted, lineHeight: 17 },
  counterRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingHorizontal: 8 },
  counterLabel: { fontSize: 13, color: tc.textMuted },
  counterValue: { fontSize: 13, fontFamily: fonts.condensedBold, color: tc.rosaText },
  counterDone: { color: colors.success },
  addBtn: { borderWidth: 1, borderStyle: 'dashed', borderColor: tc.rosaBorder, borderRadius: radius.lg, padding: 12, alignItems: 'center', marginBottom: 12 },
  addBtnText: { color: tc.rosaText, fontSize: 14, fontFamily: fonts.condensed },
  editBtn: { borderWidth: 1, borderColor: tc.border, borderRadius: radius.lg, padding: 8, alignItems: 'center', marginBottom: 12 },
  editBtnText: { color: tc.textMuted, fontSize: 13, fontFamily: fonts.condensed },
  applyRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, paddingHorizontal: 12, marginBottom: 16, backgroundColor: tc.rosaSubtle, borderRadius: radius.lg, borderWidth: 1, borderColor: tc.border },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 2, borderColor: tc.border, alignItems: 'center', justifyContent: 'center' },
  checkboxActive: { backgroundColor: tc.rosa, borderColor: tc.rosa },
  checkmark: { fontFamily: fonts.condensedBold, color: tc.brandText, fontSize: 12 },
  applyLabel: { fontSize: 13, fontFamily: fonts.condensedMedium, color: tc.text, flex: 1 },
  flipSection: { marginBottom: 16, borderTopWidth: 1, borderTopColor: tc.border, paddingTop: 16 },
  flipHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  flipTitle: { fontFamily: fonts.condensedBold, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.flip },
  flipRemove: { fontFamily: fonts.condensedBold, color: tc.textMuted, fontSize: 11 },
  flipConfirmed: { backgroundColor: colors.flipBg, borderWidth: 1, borderColor: colors.flipBorder, borderRadius: radius.lg, padding: 12, paddingHorizontal: 14 },
  flipConfirmedText: { fontFamily: fonts.condensedBold, fontSize: 12, color: colors.flip, marginBottom: 4 },
  flipConfirmedSub: { fontSize: 11, color: tc.textMuted },
  flipAddBtn: { borderWidth: 1, borderStyle: 'dashed', borderColor: tc.border, borderRadius: radius.lg, padding: 12, alignItems: 'center', marginBottom: 10 },
  flipAddBtnActive: { backgroundColor: colors.flipBg, borderColor: colors.flipBorder, borderStyle: 'solid' },
  flipAddBtnText: { color: tc.textMuted, fontSize: 13, fontFamily: fonts.condensed },
  flipAddBtnTextActive: { color: colors.flip },
  flipSlotLabel: { fontSize: 12, color: tc.textMuted, marginBottom: 8 },
  flipSlotBtn: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, paddingHorizontal: 12, borderRadius: radius.md, backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.border, marginBottom: 6 },
  flipSlotBtnActive: { backgroundColor: colors.flipBg, borderColor: colors.flip },
  flipSlotNum: { fontFamily: fonts.condensedBold, fontSize: 14, color: tc.rosaText, flexShrink: 0 },
  flipSlotNumActive: { color: colors.flip },
  flipSlotName: { flex: 1, fontSize: 13, color: tc.textMuted, fontFamily: fonts.condensed },
  flipSlotNameActive: { color: colors.flip },
})